<template>
  <div v-if="staffsResult" :class="$style.calendarForm">
    <!-- 公開用タイトル -->
    <div :class="$style.formSection">
      <p :class="$style.formSection__title">公開用タイトル</p>
      <p :class="$style.formSection__subtitle">
        日程調整画面でユーザーに表示されます。
      </p>
      <DolphinTextInput
        :class="$style.formSection__content"
        v-model="model.publicTitle"
        placeholder="例) 住まい選びのご相談"
        :disabled="isReadOnly"
      />
      <p v-if="errors?.publicTitle" :class="$style.error">
        {{ errors.publicTitle }}
      </p>
    </div>

    <!-- 管理用タイトル -->
    <div :class="$style.formSection">
      <p :class="$style.formSection__title">管理用タイトル（任意）</p>
      <p :class="$style.formSection__subtitle">管理画面でのみ表示されます。</p>
      <DolphinTextInput
        :class="$style.formSection__content"
        v-model="model.privateTitle"
        placeholder="例) 未来を見据えた住まい選び！予算とライフプランの作り方"
        :disabled="isReadOnly"
      />
      <p v-if="errors?.privateTitle" :class="$style.error">
        {{ errors.privateTitle }}
      </p>
    </div>

    <!-- 所要時間 -->
    <div :class="$style.formSection">
      <p :class="$style.formSection__title">1枠の所要時間</p>
      <div :class="$style.timeSelection">
        <ul :class="$style.radioGroup">
          <li v-for="(option, index) in durationMinutesOptions" :key="index">
            <DolphinRadioButtonInContainer
              :class="$style.radioGroup__option"
              name="durationMinutes"
              v-model="selectedDurationMinutes"
              :value="option.value"
              :disabled="isReadOnly"
            >
              {{ option.label }}
            </DolphinRadioButtonInContainer>
          </li>
        </ul>
        <div
          v-if="selectedDurationMinutes === 'custom'"
          :class="$style.customInput"
        >
          <DolphinTextInput
            :class="$style.customInput__field"
            v-model="customDurationMinutes"
            type="number"
          />
          <span :class="$style.customInput__label">分</span>
        </div>
      </div>
      <p v-if="errors?.durationMinutes" :class="$style.error">
        {{ errors.durationMinutes }}
      </p>
    </div>

    <!-- 表示間隔 -->
    <div :class="$style.formSection">
      <p :class="$style.formSection__title">枠の表示間隔</p>
      <p :class="$style.formSection__subtitle">
        自動、または所要時間と同じ値を選択すると枠が隙間なく表示されます。<br />
        値が所要時間よりも短いと枠が重複して表示されます。
      </p>
      <div :class="$style.timeSelection">
        <ul :class="$style.radioGroup">
          <li v-for="(option, index) in offsetMinutesOptions" :key="index">
            <DolphinRadioButtonInContainer
              :class="$style.radioGroup__option"
              name="offsetMinutes"
              v-model="selectedOffsetMinutes"
              :value="option.value"
              :disabled="isReadOnly"
            >
              {{ option.label }}
            </DolphinRadioButtonInContainer>
          </li>
        </ul>
        <div
          v-if="selectedOffsetMinutes === 'custom'"
          :class="$style.customInput"
        >
          <DolphinTextInput
            :class="$style.customInput__field"
            v-model="customOffsetMinutes"
            type="number"
          />
          <span :class="$style.customInput__label">分</span>
        </div>
      </div>
      <p v-if="errors?.offsetMinutes" :class="$style.error">
        {{ errors.offsetMinutes }}
      </p>
    </div>

    <!-- 表示可能日時 -->
    <div :class="$style.formSection">
      <p :class="$style.formSection__title">表示する時間範囲</p>
      <p :class="$style.formSection__subtitle">
        空きスケジュールを表示する時間帯を設定することができます。<br />
        範囲外の時間は日程調整時の候補として表示されなくなります。
      </p>
      <CalendarDailyAvailabilityTable
        :class="$style.formSection__content"
        v-model="model.dailyAvailabilities"
      />
    </div>

    <!-- スロット生成遅延時間 -->
    <div :class="$style.formSection">
      <p :class="$style.formSection__title">何時間後の枠から表示するか</p>
      <p :class="$style.formSection__subtitle">
        日時候補を確認するタイミングを基準として、直近何時間を候補として表示しないかを設定できます。<br />
        ※ 0時間に設定したとき、直後の選択可能な枠から表示されます。
      </p>
      <div :class="$style.timeSelection">
        <ul :class="$style.radioGroup">
          <li v-for="(option, index) in delayHoursOptions" :key="index">
            <DolphinRadioButtonInContainer
              :class="$style.radioGroup__option"
              name="slotGenerationDelayHours"
              v-model="selectedDelayHours"
              :value="option.value"
              :disabled="isReadOnly"
            >
              {{ option.label }}
            </DolphinRadioButtonInContainer>
          </li>
        </ul>
        <div v-if="selectedDelayHours === 'custom'" :class="$style.customInput">
          <DolphinTextInput
            :class="$style.customInput__field"
            v-model="customDelayHours"
            type="number"
            :disabled="isReadOnly"
          />
          <span :class="$style.customInput__label">時間</span>
        </div>
      </div>
      <p v-if="errors?.slotGenerationDelayHours" :class="$style.error">
        {{ errors.slotGenerationDelayHours }}
      </p>
    </div>

    <!-- スロット生成期間 -->
    <div :class="$style.formSection">
      <p :class="$style.formSection__title">何日間分の枠を表示するか</p>
      <p :class="$style.formSection__subtitle">
        表示する日程候補を何日間分表示するかを設定できます。
      </p>
      <div :class="$style.timeSelection">
        <ul :class="$style.radioGroup">
          <li v-for="(option, index) in periodDaysOptions" :key="index">
            <DolphinRadioButtonInContainer
              :class="$style.radioGroup__option"
              name="slotGenerationPeriodDays"
              v-model="selectedPeriodDays"
              :value="option.value"
              :disabled="isReadOnly"
            >
              {{ option.label }}
            </DolphinRadioButtonInContainer>
          </li>
        </ul>
        <div v-if="selectedPeriodDays === 'custom'" :class="$style.customInput">
          <DolphinTextInput
            :class="$style.customInput__field"
            v-model="customPeriodDays"
            type="number"
            :disabled="isReadOnly"
          />
          <span :class="$style.customInput__label">日間</span>
        </div>
      </div>
      <p v-if="errors?.slotGenerationPeriodDays" :class="$style.error">
        {{ errors.slotGenerationPeriodDays }}
      </p>
    </div>

    <!-- staff の割り当て -->
    <div :class="$style.formSection">
      <p :class="$style.formSection__title">担当者</p>

      <!-- Staff Selection Strategy -->
      <p :class="[$style.formSection__subtitle, $style['formSection--spaced']]">
        担当者の選出方法を選択してください。<br />
        ・ランダム:
        選択された担当者候補の中から空いている人がランダムに1人選ばれます<br />
        ・優先度順: 設定した優先度が高い順に担当者が選ばれます<br />
      </p>
      <ul :class="[$style.radioGroup, $style['formSection--spaced']]">
        <li>
          <DolphinRadioButtonInContainer
            :class="$style['radioGroup__option--wide']"
            name="staffSelectionStrategy"
            v-model="model.staffSelectionStrategy"
            :value="CalendarStaffSelectionStrategy.Random"
            :disabled="isReadOnly"
          >
            {{
              getStaffSelectionStrategyLabel(
                CalendarStaffSelectionStrategy.Random
              )
            }}
          </DolphinRadioButtonInContainer>
        </li>
        <li>
          <DolphinRadioButtonInContainer
            :class="$style['radioGroup__option--wide']"
            name="staffSelectionStrategy"
            v-model="model.staffSelectionStrategy"
            :value="CalendarStaffSelectionStrategy.Priority"
            :disabled="isReadOnly"
          >
            {{
              getStaffSelectionStrategyLabel(
                CalendarStaffSelectionStrategy.Priority
              )
            }}
          </DolphinRadioButtonInContainer>
        </li>
      </ul>
      <p
        v-if="errors?.staffSelectionStrategy"
        :class="[$style.error, $style['formSection--spaced']]"
      >
        {{ errors.staffSelectionStrategy }}
      </p>

      <p :class="[$style.formSection__subtitle, $style['formSection--spaced']]">
        担当者候補を1人以上選択してください。<br />
        ※ 選択肢に表示されるのはカレンダー連携している人のみです
      </p>

      <!-- Staff Selection / Priority UI -->
      <div v-if="model.staffSelectionStrategy === 'RANDOM'">
        <DolphinMultiSelector
          :class="[$style.staffSelection, $style['formSection--spaced']]"
          v-model="assignedStaffs"
          :options="staffOptions"
          track-by="guid"
          label="displayName"
          :disabled="isReadOnly"
        />
      </div>
      <div v-else-if="model.staffSelectionStrategy === 'PRIORITY'">
        <DraggableStaffPrioritySelector
          :class="$style['formSection--spaced']"
          v-model="assignedStaffs"
          :avail-staffs="staffOptions"
          :disabled="isReadOnly"
        />
      </div>
      <p
        v-if="errors?.assignedStaffs"
        :class="[$style.error, $style['formSection--spaced']]"
      >
        {{ errors.assignedStaffs }}
      </p>
    </div>

    <!-- カレンダーの公開・非公開ステータス -->
    <div :class="$style.formSection">
      <p :class="$style.formSection__title">カレンダーの状態</p>
      <p :class="$style.formSection__subtitle">
        非公開にすると日程調整カレンダーの指定画面で候補として表示されなくなります。
      </p>
      <p :class="$style.formSection__warning">
        ※
        非公開にすると設定済みの日程調整でユーザーが予約できなくなります。非公開にする場合は、他で設定されていないか必ず確認してください。
      </p>
      <div :class="$style.statusSelection">
        <ul :class="$style.radioGroup">
          <li v-for="option in statusOptions" :key="option.value">
            <DolphinRadioButtonInContainer
              :class="$style.radioGroup__option"
              name="status"
              v-model="calendarStatus"
              :value="option.value"
              :disabled="isReadOnly"
            >
              {{ option.label }}
            </DolphinRadioButtonInContainer>
          </li>
        </ul>
      </div>
    </div>
    <CalendarStatusModal disable-backdrop>
      <ConfirmationModal
        title="カレンダーの状態を 非公開 へ変更"
        buttonText="非公開にする"
        @confirm="handleConfirm"
        @close="handleClose"
      >
        <p>
          非公開にすると、
          <span :class="$style.warning"
            >設定済みの日程調整でユーザーが予約できなくなります</span
          >
          。<br />
          本当に非公開にしてもよろしいですか？
        </p>
      </ConfirmationModal>
    </CalendarStatusModal>

    <!-- 予約時の質問事項 -->
    <div :class="$style.formSection">
      <p :class="$style.formSection__title">予約時の質問事項</p>
      <p :class="$style.formSection__warning">
        ※ こちらの質問事項は公開URL経由の日程調整画面でのみ表示されます。<br />
        その他の導線から予約された場合、任意の質問事項は空のままとなり、必須のテキストは「チャット画面からの予約」、必須のセレクトボックスは一番上の選択肢が選択された状態で予約されます。<br />
      </p>

      <CalendarQuestionsForm
        :class="$style.formSection__content"
        v-model="model.questions"
        :formErrors="errors?.questions ?? []"
      />
    </div>

    <!-- カレンダーの説明 -->
    <div :class="$style.formSection">
      <p :class="$style.formSection__title">カレンダーの説明</p>
      <p :class="$style.formSection__subtitle">
        ※ 管理用です。ユーザーには表示されません。
      </p>
      <CountingTextarea
        :class="$style.formSection__content"
        :rows="8"
        v-model="model.description"
        :disabled="isReadOnly"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import DolphinMultiSelector from '@/components/atoms/DolphinMultiSelector.vue'
import DolphinRadioButtonInContainer from '@/components/molecules/DolphinRadioButtonInContainer.vue'
import CountingTextarea from '@/components/molecules/CountingTextarea.vue'
import ConfirmationModal from '@/components/molecules/ConfirmationModal.vue'
import { useModal } from '@/hooks/useModal.ts'

import { type FormType, validateForm } from './formSchema'
import { getStaffSelectionStrategyLabel, statusOptions } from './calendar.ts'
import CalendarDailyAvailabilityTable from './CalendarDailyAvailabilityTable.vue'
import DraggableStaffPrioritySelector from './DraggableStaffPrioritySelector.vue'
import { useCalendarFormStaffsQuery } from './CalendarForm.generated.ts'
import { CalendarStaffSelectionStrategy } from '@/types/schema.generated.ts'
import CalendarQuestionsForm from './questions/CalendarQuestionsForm.vue'

const props = defineProps<{
  canEdit?: boolean
}>()

const model = defineModel<FormType>({ required: true })

const isReadOnly = computed(() => !props.canEdit)

const errors = computed(() => validateForm(model.value))

/****************************************************************
 * Slot Duration Minutes
 ****************************************************************/

const durationMinutesOptions = [
  {
    label: '30分',
    value: 30,
  },
  {
    label: '50分',
    value: 50,
  },
  {
    label: '60分',
    value: 60,
  },
  {
    label: 'カスタム',
    value: 'custom',
  },
]

const durationMinutesPresets = durationMinutesOptions
  .filter(option => typeof option.value === 'number')
  .map(option => option.value as number)

const selectedDurationMinutes = ref<number | string>(
  durationMinutesPresets.includes(model.value.durationMinutes)
    ? model.value.durationMinutes
    : 'custom'
)
const customDurationMinutes = ref<number>(
  durationMinutesPresets.includes(model.value.durationMinutes)
    ? 60
    : model.value.durationMinutes
)

watch([selectedDurationMinutes, customDurationMinutes], newVal => {
  if (typeof newVal[0] === 'number') {
    model.value.durationMinutes = newVal[0]
  } else {
    model.value.durationMinutes = newVal[1]
  }
})

/****************************************************************
 * Slot Offset Minutes
 ****************************************************************/

const offsetMinutesOptions = [
  {
    label: '自動',
    value: 'auto',
  },
  {
    label: '30分',
    value: 30,
  },
  {
    label: '60分',
    value: 60,
  },
  {
    label: 'カスタム',
    value: 'custom',
  },
]

const offsetMinutesPresets = offsetMinutesOptions
  .filter(option => typeof option.value === 'number')
  .map(option => option.value as number)

const selectedOffsetMinutes = ref<number | string>(
  model.value.offsetMinutes === model.value.durationMinutes
    ? 'auto'
    : offsetMinutesPresets.includes(model.value.offsetMinutes)
      ? model.value.offsetMinutes
      : 'custom'
)
const customOffsetMinutes = ref<number>(
  model.value.offsetMinutes === model.value.durationMinutes
    ? 60
    : offsetMinutesPresets.includes(model.value.offsetMinutes)
      ? 60
      : model.value.offsetMinutes
)

watch([selectedOffsetMinutes, customOffsetMinutes], newVal => {
  if (typeof newVal[0] === 'number') {
    model.value.offsetMinutes = newVal[0]
    return
  }

  if (newVal[0] === 'auto') {
    model.value.offsetMinutes = model.value.durationMinutes
    return
  }

  model.value.offsetMinutes = newVal[1]
})

/****************************************************************
 * Slot Generation Delay Hours
 ****************************************************************/

const delayHoursOptions = [
  {
    label: '2時間',
    value: 2,
  },
  {
    label: '18時間',
    value: 18,
  },
  {
    label: '24時間',
    value: 24,
  },
  {
    label: 'カスタム',
    value: 'custom',
  },
]

const delayHoursPresets = delayHoursOptions
  .filter(option => typeof option.value === 'number')
  .map(option => option.value as number)

const selectedDelayHours = ref<number | string>(
  delayHoursPresets.includes(model.value.slotGenerationDelayHours)
    ? model.value.slotGenerationDelayHours
    : 'custom'
)
const customDelayHours = ref<number>(
  delayHoursPresets.includes(model.value.slotGenerationDelayHours)
    ? 18
    : model.value.slotGenerationDelayHours
)

watch([selectedDelayHours, customDelayHours], newVal => {
  if (typeof newVal[0] === 'number') {
    model.value.slotGenerationDelayHours = newVal[0]
  } else {
    model.value.slotGenerationDelayHours = newVal[1]
  }
})

/****************************************************************
 * Slot Generation Period Days
 ****************************************************************/

const periodDaysOptions = [
  {
    label: '7日間',
    value: 7,
  },
  {
    label: '14日間',
    value: 14,
  },
  {
    label: '30日間',
    value: 30,
  },
  {
    label: 'カスタム',
    value: 'custom',
  },
]

const periodDaysPresets = periodDaysOptions
  .filter(option => typeof option.value === 'number')
  .map(option => option.value as number)

const selectedPeriodDays = ref<number | string>(
  periodDaysPresets.includes(model.value.slotGenerationPeriodDays)
    ? model.value.slotGenerationPeriodDays
    : 'custom'
)
const customPeriodDays = ref<number>(
  periodDaysPresets.includes(model.value.slotGenerationPeriodDays)
    ? 14
    : model.value.slotGenerationPeriodDays
)

watch([selectedPeriodDays, customPeriodDays], newVal => {
  if (typeof newVal[0] === 'number') {
    model.value.slotGenerationPeriodDays = newVal[0]
  } else {
    model.value.slotGenerationPeriodDays = newVal[1]
  }
})

/****************************************************************
 * Calendar Status
 ****************************************************************/
const {
  CalendarStatusModal,
  openCalendarStatusModal,
  closeCalendarStatusModal,
} = useModal('calendarStatus')

const calendarStatus = computed({
  get: () => {
    return model.value.status
  },

  set: newVal => {
    if (newVal === model.value.status) {
      return
    }

    // 条件によって値の変更を行わない処理にした場合、
    // radio button が押せなくなるので条件に依らず値をセットする
    model.value.status = newVal

    if (newVal === 'INACTIVE') {
      // INACTIVE の場合は確認モーダルを表示し、その先の回答に応じて値をセットする
      openCalendarStatusModal()
    }
  },
})

// 確認ボタンが押されず close が押された場合は 'ACTIVE' に戻す
const handleClose = () => {
  model.value.status = 'ACTIVE'
  closeCalendarStatusModal()
}

// 確認ボタンが押された場合はそのまま
const handleConfirm = () => {
  closeCalendarStatusModal()
}

/****************************************************************
 * Staffs
 ****************************************************************/

const { result: staffsResult } = useCalendarFormStaffsQuery({
  fetchPolicy: 'no-cache',
})

const staffOptions = computed(() => {
  if (!staffsResult.value) {
    return []
  }

  // カレンダー連携しているスタッフのみ選択肢として表示
  return staffsResult.value.staffsByPlanner
    .filter(staff => staff.connectedThirdPartyProvider)
    .map(staff => ({
      guid: staff.guid,
      displayName: staff.displayName,
    }))
})

const assignedStaffs = computed({
  get: () => {
    if (!model.value) {
      return []
    }

    // カレンダー連携していないスタッフは割り当てスタッフから削除
    const availStaffGUIDs = staffOptions.value.map(s => s.guid)
    return model.value.assignedStaffs.filter(staff =>
      availStaffGUIDs.includes(staff.guid)
    )
  },
  set: newValue => {
    model.value.assignedStaffs = newValue
  },
})
</script>

<style lang="scss" module>
.error {
  color: $color-caution-accent;
  font-size: 12px;

  &:nth-child(n + 2) {
    margin-top: 4px;
  }
}

.warning {
  color: $color-caution-accent;
  font-weight: bold;
}

.calendarForm {
  // Main container styles
}

.formSection {
  &:nth-child(n + 2) {
    margin-top: 40px;
  }

  &__title {
    font-size: 18px;
    font-weight: bold;
    color: $color-text-and-object-medium-emphasis;
  }

  &__subtitle {
    font-size: 12px;
    color: $color-text-and-object-high-emphasis;
    margin-top: 8px;
  }

  &__warning {
    font-size: 12px;
    color: $color-caution-accent;
    margin-top: 8px;
  }

  &__content {
    margin-top: 16px;
    width: 100%;
  }

  &--spaced {
    margin-top: 16px;
  }
}

.timeSelection {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin-top: 16px;
}

%radio-button-option-base {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
}

.radioGroup {
  display: flex;
  gap: 2px;
  margin-top: 16px;

  &__option {
    @extend %radio-button-option-base;

    width: 90px;

    &--wide {
      @extend %radio-button-option-base;

      width: 120px;
    }
  }
}

.customInput {
  display: flex;
  align-items: center;

  &__field {
    width: 90px;
    line-height: 1.4;
  }

  &__label {
    margin-left: 12px;
  }
}

.staffSelection {
  width: 60%;

  &__placeholder {
    padding: 20px;
    border: 1px dashed $color-border-medium-emphasis;
    background-color: $color-surface-secondary;
    color: $color-text-and-object-medium-emphasis;
    text-align: center;
    border-radius: $border-radius-v2-base;
    margin-bottom: 16px;
  }
}

.statusSelection {
  margin-top: 16px;
}
</style>
